/**
 * Dragging a block's card onto another's to merge them (Idea #438; ADR 0118),
 * by the same rule as the Merge button (`mergeCheck`), which stays for the
 * keyboard. While a card is dragged over another, that card says whether a
 * drop would merge them, and if not, why. Only a block still to log can be
 * dragged.
 *
 * And onto an open ticket (v0.9.0's P4): a logged block's card whose ticket
 * is open, a dashed card of time entered in Autotask on one, or (P5) a row of
 * the open-tickets tray (`ticketCheck`). "Log 25m to ticket T… (open)", or why
 * not. **A drop writes nothing to Autotask:** it opens the dragged block's
 * Log form on that ticket (`onLog`). A block without a client takes the
 * ticket's first, as a merge gives it the other's, so its form can show.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type DragEvent, useState } from "react";

import { ApiError } from "../../api/client";
import { useSession } from "../../app/session-context";
import { type BlockView, type DropCheck, type TicketTarget, dropCheck, ticketCheck } from "./model";

/** What a dragged card carries: its block's ID. */
export const DRAG_TYPE = "application/x-axon-time-block";

/** What a card shows while another is dragged over it, or is merging into it. */
export type DropHint = DropCheck | { merging: true };

/** Where a dragged block can be dropped: what it says, and its drag events. */
export interface DropZone {
  hint: DropHint | null;
  onDragEnter: (event: DragEvent<HTMLElement>) => void;
  onDragOver: (event: DragEvent<HTMLElement>) => void;
  onDragLeave: (event: DragEvent<HTMLElement>) => void;
  onDrop: (event: DragEvent<HTMLElement>) => void;
}

export interface CardDrag extends DropZone {
  draggable: boolean;
  /** This card is the one being dragged. */
  dragging: boolean;
  onDragStart: (event: DragEvent<HTMLElement>) => void;
  onDragEnd: () => void;
}

function refusal(error: Error): string {
  if (error instanceof ApiError && error.status === 403) {
    return "Couldn't merge them: you can only change your own blocks.";
  }
  if (error instanceof ApiError && error.detail) {
    // The server's reason, e.g. one of them was logged meanwhile.
    return `Couldn't merge them: ${error.detail}.`;
  }
  return `Couldn't merge them. ${error.message}`;
}

/** Why a dropped block without a client couldn't take its ticket's. */
function notTaken(error: Error): string {
  if (error instanceof ApiError && error.status === 403) {
    return "Couldn't give it the ticket's client: you can only change your own blocks.";
  }
  if (error instanceof ApiError && error.status === 409) {
    return "Couldn't give it the ticket's client: this block can't change any more.";
  }
  return `Couldn't give it the ticket's client. ${error instanceof ApiError && error.detail ? error.detail : error.message}`;
}

/** Whether a drop changes something: merges, or logs to the ticket. */
function allowed(check: DropCheck): boolean {
  return "logs" in check ? check.logs : check.merges;
}

export function useDragToMerge(
  blocks: readonly BlockView[],
  onMerged: (keptId: string) => void,
  /** A block dropped on an open ticket: open its Log form there (v0.9.0's P4). */
  onLog?: (blockId: string, ticket: TicketTarget) => void,
) {
  const { api } = useSession();
  const queryClient = useQueryClient();
  const [draggedId, setDraggedId] = useState<string | null>(null);
  // The card, dashed card or tray row it's over, by its own key.
  const [overKey, setOverKey] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: ({ dragged, onto }: { dragged: string; onto: string }) => api.mergeBlocks(onto, dragged),
    onSuccess: async (merged) => {
      // The earlier block is kept, whichever was dragged (ADR 0062).
      onMerged(merged.block_id);
      await queryClient.invalidateQueries({ queryKey: ["day"] });
      // The Log form's preview is built from the block's activities (#224).
      await queryClient.invalidateQueries({ queryKey: ["submission-preview"] });
    },
  });
  // A block without a client, dropped on a ticket, takes the ticket's client
  // first (ADR 0033's assignment, without remembering its number): in Axon
  // Time only, as Change client would. Its Log form opens once the day has it.
  const take = useMutation({
    mutationFn: async ({ dragged, ticket }: { dragged: BlockView; ticket: TicketTarget }) => {
      if (dragged.clientId === null && ticket.clientId !== null) {
        await api.assignClient(dragged.id, ticket.clientId, false);
        await queryClient.invalidateQueries({ queryKey: ["day"] });
      }
    },
    onSuccess: (_, { dragged, ticket }) => {
      onLog?.(dragged.id, ticket);
    },
  });
  const dragged = blocks.find((b) => b.id === draggedId) ?? null;

  const end = () => {
    setDraggedId(null);
    setOverKey(null);
  };

  /** A drop zone for `key`, by `check` of the dragged block; `act` does the drop. */
  function zone(key: string, check: DropCheck | null, act: (check: DropCheck) => void, merging = false): DropZone {
    const over = (event: DragEvent<HTMLElement>) => {
      if (check === null) {
        return;
      }
      setOverKey(key);
      // A drop is allowed only where it does something: elsewhere the browser shows "no drop".
      if (allowed(check)) {
        event.preventDefault();
        event.dataTransfer.dropEffect = "move";
      }
    };
    return {
      hint: merging ? { merging: true } : overKey === key ? check : null,
      // Over again with each dragover too: a browser that leaves out where a
      // drag went on dragleave clears it as the pointer crosses the card's text.
      onDragEnter: over,
      onDragOver: over,
      onDragLeave: (event) => {
        // Not when it only moves onto something inside the card.
        if (!(event.relatedTarget instanceof Node && event.currentTarget.contains(event.relatedTarget))) {
          setOverKey((current) => (current === key ? null : current));
        }
      },
      onDrop: (event) => {
        event.preventDefault();
        if (check !== null && allowed(check)) {
          act(check);
        }
        end();
      },
    };
  }

  /** Open its Log form on the ticket it was dropped on. */
  const logTo = (check: DropCheck) => {
    if (dragged !== null && "logs" in check && check.logs && !take.isPending) {
      mutation.reset();
      take.mutate({ dragged, ticket: check.ticket });
    }
  };

  function cardDrag(block: BlockView): CardDrag {
    const check = dragged && dragged.id !== block.id ? dropCheck(dragged, block) : null;
    const merging = mutation.isPending && mutation.variables.onto === block.id;
    return {
      ...zone(
        `block:${block.id}`,
        check,
        (done) => {
          if ("logs" in done) {
            logTo(done);
          } else if (dragged !== null) {
            mutation.mutate({ dragged: dragged.id, onto: block.id });
          }
        },
        merging,
      ),
      draggable: block.canDismiss,
      dragging: dragged?.id === block.id,
      onDragStart: (event) => {
        if (!block.canDismiss) {
          return;
        }
        event.dataTransfer.setData(DRAG_TYPE, block.id);
        event.dataTransfer.effectAllowed = "move";
        mutation.reset();
        take.reset();
        setDraggedId(block.id);
      },
      onDragEnd: end,
    };
  }

  /**
   * A ticket that isn't a block's card as a drop target: a dashed card of time
   * entered in Autotask, or (P5) a tray row. `key` tells it from the others.
   */
  function ticketDrop(key: string, ticket: TicketTarget): DropZone {
    return zone(key, dragged ? ticketCheck(dragged, ticket) : null, logTo);
  }

  const error = mutation.isError ? refusal(mutation.error) : take.isError ? notTaken(take.error) : null;
  return { cardDrag, ticketDrop, dragging: dragged !== null, error };
}
